import ThemeContext from "../Context/ThemeContext";
import React, { FC, useEffect, useState } from "react";
import { Theme } from "../Themes/Themes";
import axios, { AxiosError } from "axios";
import { ApplicationJsonConfigHeader, BaseUrl } from "../Api/axios.config";

// Environment configuration
const USE_DEFAULT_THEME = process.env.REACT_APP_USE_DEFAULT_THEME === "true";
const DEFAULT_GENDER = (process.env.REACT_APP_DEFAULT_GENDER || "Male") as "Male" | "Female";

// Default theme for local development and initial load
const DEFAULT_THEME = DEFAULT_GENDER === "Male" ? Theme.blueMode : Theme.pinkMode;
const THEME_CACHE_KEY = "user_theme_cache";

const ThemeProvider: FC = ({ children }) => {
  // Initialize with cached theme or default theme to prevent blank screen
  const [gender, setCurrGender] = useState<"Male" | "Female" | undefined>(() => {
    // If using default theme in local dev, return immediately
    if (USE_DEFAULT_THEME) {
      return DEFAULT_GENDER;
    }
    // Try to get cached gender from localStorage
    const cached = localStorage.getItem(THEME_CACHE_KEY);
    return cached as "Male" | "Female" | undefined;
  });
  const [isLoading, setIsLoading] = useState(!USE_DEFAULT_THEME);

  useEffect(() => {
    // Skip API call if using default theme for local development
    if (!USE_DEFAULT_THEME) {
      updateTheme();
    }
  }, []);

  const updateTheme = async () => {
    // If using default theme, don't make API calls
    if (USE_DEFAULT_THEME) {
      console.log("Using default theme from environment configuration");
      return;
    }

    setIsLoading(true);
    try {
      const fetchedGender = await getGender();
      setCurrGender(fetchedGender);
      // Cache the gender for faster subsequent loads
      localStorage.setItem(THEME_CACHE_KEY, fetchedGender);
    } catch (error) {
      console.error("Failed to fetch theme:", error);
      // If fetching fails and no cached value, use default
      if (!gender) {
        setCurrGender(DEFAULT_GENDER);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Always provide a theme - use cached, fetched, or default
  const currentTheme =
    gender === "Male"
      ? Theme.blueMode
      : gender === "Female"
      ? Theme.pinkMode
      : DEFAULT_THEME;

  return (
    <ThemeContext.Provider
      value={{
        theme: currentTheme,
        getTheme: updateTheme,
        isLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

const getGender = async (): Promise<"Male" | "Female"> => {
  try {
    const token: string | null = localStorage.getItem("token");
    const response = await axios.get(BaseUrl + "/authentication/getGender", {
      headers: {
        ...ApplicationJsonConfigHeader,
        authorization: `Bearer ${token}`,
      },
      responseType: "json",
    });
    return response.data.gender;
  } catch (e) {
    const err = e as AxiosError;
    const { error } = (err.response?.data as { error: string }) || {};
    console.log(error);
    return "Male";
  }
};

export default ThemeProvider;
